<template>
  <div>
    <template v-if="state == 'submitting'">
      <div class="loader" />
      <div class="overlay" />
    </template>
    <div class="cc-field" :style="{height: height +'px'}">
      <CreditCardField ref="card" @input="handleChange" @valid="onValid" @invalid="onInvalid" :key="version"></CreditCardField>
    </div>
  </div>
</template>
<script>
import validator from 'card-validator';
import uuid from "uuid";
import Vue from 'vue';
import CreditCardField from "vue-credit-card-field";

const validateNumber = CreditCardField.InlineCreditCardField.methods.validateNumber
CreditCardField.CreditCardField.methods.validateNumber = (num) => {
  const digits = (num + '').replace(/\s+|-/g, '');
  if (digits.length == 13) return false
  
  return validateNumber(num)
}

const globalState = Vue.observable({
  cvvRequired: true
})

CreditCardField.CreditCardField.computed.totalFields = () => globalState.cvvRequired ? 4 : 3

export default {
  data() {
    const params = Object.fromEntries(location.search.replace('?','').split('&').map(x=>x.split('=')))

    return {
      requireCVV: true,
      state: "idle",
      card: null,
      height: params.height,
      version: 0,
      rawMode: false
    };
  },
  mounted() {
    this.$el.querySelector("[placeholder='Credit Card Number']").autocomplete = 'cc-number'
    
    const params = Object.fromEntries(location.search.replace('?','').split('&').map(x=>x.split('=')))

    if (params.noCVV) this.removeCVV()

    const card = this.$refs.card

    card.$options.methods.validateCvc = (cvv) => {
      if (!this.requireCVV) return true

      return card.brand === 'amex' && cvv.length == 4 || cvv.length == 3
    }

    this.initialState = this.$refs.card.$data
    
    this.$watch(() => card.showSecurityFields, () => {
      parent.postMessage(
          JSON.stringify({
            event: "resize",
            payload: true
          }),
           "*"
        )
    })
  },
  components: {
    CreditCardField: CreditCardField.CreditCardField
  },
  watch: {
    state: {
      immediate: true,
      handler() {
        parent.postMessage(
          JSON.stringify({
            event: "state",
            payload: { state: this.state }
          }),
           "*"
        )
      }
    }
  },
  methods: {
    setRawMode (rawMode) {
      this.rawMode = rawMode

      if (rawMode) {
        parent.postMessage(
          JSON.stringify({
            event: "card",
            payload: this.state == 'valid' ? {card: {cvc: this.card.cvc, month: this.card.expMonth, year: this.card.expYear, number: this.card.number}} : {valid: false}
          }),
          "*"
        )
      }
    },
    onValid () {
      const { cvc, expMonth, expYear, number } = this.card
      this.state = ((this.requireCVV && cvc) || !this.requireCVV) &&  !!expMonth && !!expYear && number ? 'valid' : 'invalid'
    },
    onInvalid() {
      this.state = 'invalid'
    },
    async handleChange({cvc, expiration, number} ) {
      const [expMonth,expYear] = (expiration || '').split('/')

      if (this.rawMode) {
        if (!number) {
          parent.postMessage(
            JSON.stringify({
              event: "card",
              payload: null
            }),
            "*"
          )

          return
        }
        
        const validNumber = validator.number(number)
        const validExpiration = validator.expirationDate(expiration)

        const valid = validNumber.isValid && validExpiration.isValid && (!this.requireCVV || !!cvc)

        parent.postMessage(
          JSON.stringify({
            event: "card",
            payload: valid ? {card: {cvc, month: validExpiration.month, year: validExpiration.year, number: number.replace(/ /g, '')}} : {valid: false}
          }),
          "*"
        )
      }

      if (this.state == 'submitting') return;

      this.card = {
        number: (number || '').replace(/ /g, ''),
        cvc,
        expMonth: (expMonth || '').trim(),
        expYear
      }
    },
    async reset () {
      this.state = 'idle'
      this.version++
      this.card = null
    },
    removeCVV () {
      var style=document.createElement('style');
      style.type='text/css';
      if(style.styleSheet){
          style.styleSheet.cssText='.credit-card-field-cvc { display: none }';
      }else{
          style.appendChild(document.createTextNode('.credit-card-field-cvc { display: none }'));
      }
      document.getElementsByTagName('head')[0].appendChild(style);
      
      this.requireCVV = false

      globalState.cvvRequired = false
      
      
    },
    async createToken(input, test_mode = false, raw_mode = false) {
      if (this.state !== "valid") return;

      if (raw_mode) {
        return {
          expMonth: this.card.expMonth,
          expYear: this.card.expYear,
          card: this.card.number.replace(/\s/g, "")
        }
      }
      this.state = "submitting";

      try {
        const result = await fetch("https://payment.ouapis.org/api/payment/process", {
          method: "POST",
          body: JSON.stringify({
            test_mode,
            Gateway: input.gateway,
            Description: input.description,
            TransactionID: uuid.v4(),
            Currency: "USD",
            amount: 0,
            transaction_type: "purchasesave",
            AccountID: input.AccountID,
            email: input.email,
            referer_url: input.referer_url,
            Card: {
              NameOnAccount: `${input.FirstName} ${input.LastName}`,
              fname: input.FirstName,
              lname: input.LastName,
              ExpirationMonth: this.card.expMonth,
              ExpirationYear: String(this.card.expYear).length === 2 ? `20${this.card.expYear}` : this.card.expYear,
              CardNumber: this.card.number.replace(/\s/g, ""),
              SecurityCode: this.card.cvc,
              Address: {
                Street: input.street,
                Street2: input.street2,
                City: input.city,
                State: input.state,
                Zip: input.zip,
                Country: input.country,
                Phone: input.phone
              }
            }
          }),
          headers: {
            "Content-Type": "application/json"
          }
        }).then(x => x.json())

      if (!result.success) {
        return {
          error: result.message
        }
      }

      return {
          token: result.token,
          expMonth: this.card.expMonth,
          expYear: this.card.expYear,
          card: result.transaction.cc_number
        }
      } catch (error) {
        return {
          error: error.toString()
        }
      }
      finally {
        this.state = "idle";
      }

    }
  }
};
</script>
<style>
body {
  background: none;
}
</style>
<style scoped>
.wrapper {
  position: relative
}

.overlay {
  position: absolute;
  top: 0;
  right: 0;
  background: black;
  height: 38px;
  width: 100%;
  z-index: 100;
  opacity: .05;
}

/* https://projects.lukehaas.me/css-loaders/ */
.loader,
.loader:before,
.loader:after {
  border-radius: 50%;
  width: 2.5em;
  height: 2.5em;
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
  -webkit-animation: load7 1.8s infinite ease-in-out;
  animation: load7 1.8s infinite ease-in-out;
}
.loader {
  color: black;
  font-size: 7px;
  /*margin: 80px auto;*/
  position: absolute;
  text-indent: -9999em;
  -webkit-transform: translateZ(0);
  -ms-transform: translateZ(0);
  transform: translateZ(0);
  -webkit-animation-delay: -0.16s;
  animation-delay: -0.16s;
  right: 50%;
  z-index: 1000
}
.loader:before,
.loader:after {
  content: '';
  position: absolute;
  top: 0;
}
.loader:before {
  left: -3.5em;
  -webkit-animation-delay: -0.32s;
  animation-delay: -0.32s;
}
.loader:after {
  left: 3.5em;
}
@-webkit-keyframes load7 {
  0%,
  80%,
  100% {
    box-shadow: 0 2.5em 0 -1.3em;
  }
  40% {
    box-shadow: 0 2.5em 0 0;
  }
}
@keyframes load7 {
  0%,
  80%,
  100% {
    box-shadow: 0 2.5em 0 -1.3em;
  }
  40% {
    box-shadow: 0 2.5em 0 0;
  }
}
.cc-field >>> .credit-card-field-wrapper.form-group {
  margin-bottom: 0;
}
.cc-field >>> .credit-card-field>.form-group {
  background: none;
}
.cc-field >>> .credit-card-field,
.cc-field >>> .credit-card-field-icon-wrapper {
  background: transparent !important;
}
.cc-field >>> .credit-card-field.is-focused {
  outline: none;
  box-shadow: 0 0 3px rgba(56, 93, 163, 0.5) !important;
  border-color: #385da3 !important;
  background: white !important;
}
.cc-field >>> .credit-card-field-wrapper .form-control.credit-card-field {
  border: 1px solid #e5e7f1;
}
.cc-field >>> .form-control {
  font-size: 14px
}
.cc-field >>> .credit-card-field .custom-select-field>label {
  z-index: 100;
}
</style>